/**
 * Sprint 24.15 — HireWizardEntry (step 1 of the hire wizard).
 *
 * The fork: "Hire from template" (recommended) vs "Build from scratch" (the
 * existing Sprint 23.5 composition flow). Picking template → gallery; picking
 * scratch → the conversational composition surface. Per the Sprint 24.15 visual
 * contract (recommended card = 2px info border + "Recommended" badge).
 */

import type { CSSProperties } from "react";

export interface HireWizardEntryProps {
  onPickTemplate: () => void;
  onPickScratch: () => void;
}

const TEMPLATE_CHIPS = ["Architect", "Support", "Sales", "Marketing", "PM"];

export function HireWizardEntry({ onPickTemplate, onPickScratch }: HireWizardEntryProps) {
  return (
    <div style={body}>
      <header style={headerWrap}>
        <h3 style={h3}>How do you want to start?</h3>
        <div style={subtitle}>Pick a ready-made template, or compose an agent from scratch.</div>
      </header>

      <div style={cardGrid}>
        {/* Recommended — Hire from template */}
        <button type="button" onClick={onPickTemplate} style={{ ...card, ...recommendedCard }}>
          <span style={badge}>Recommended</span>
          <div style={{ ...iconBox, background: "rgba(127, 119, 221, 0.14)", color: "#7F77DD" }} aria-hidden="true">
            <i className="ti ti-stack-2" style={{ fontSize: 20 }} />
          </div>
          <div style={cardTitle}>Hire from template</div>
          <div style={cardDesc}>
            Start from a proven agent — persona, instructions, and equipment already set. Customise before hiring.
          </div>
          <div style={chipRow}>
            {TEMPLATE_CHIPS.map((c) => (
              <span key={c} style={chip}>
                {c}
              </span>
            ))}
          </div>
          <span style={{ ...cta, color: "var(--color-text-info)" }}>
            Browse templates <i className="ti ti-arrow-right" aria-hidden="true" />
          </span>
        </button>

        {/* Build from scratch */}
        <button type="button" onClick={onPickScratch} style={{ ...card, ...scratchCard }}>
          <div style={{ ...iconBox, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)" }} aria-hidden="true">
            <i className="ti ti-pencil-plus" style={{ fontSize: 20 }} />
          </div>
          <div style={cardTitle}>Build from scratch</div>
          <div style={cardDesc}>
            Compose a brand-new agent in conversation — shape its persona, voice, and specialties together.
          </div>
          <span style={{ ...cta, color: "var(--color-text-secondary)" }}>
            Start composing <i className="ti ti-arrow-right" aria-hidden="true" />
          </span>
        </button>
      </div>

      <div style={infoBanner}>
        <i className="ti ti-info-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden="true" />
        <span>Templates are a head start — you can still rename, re-equip, and add instructions before hiring.</span>
      </div>
    </div>
  );
}

const body: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 20px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const headerWrap: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const h3: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 500, color: "var(--color-text-primary)" };
const subtitle: CSSProperties = { fontSize: 14, color: "var(--color-text-secondary)" };

const cardGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

const card: CSSProperties = {
  position: "relative",
  minHeight: 280,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  alignItems: "flex-start",
  textAlign: "left",
  padding: "20px 18px",
  borderRadius: "var(--border-radius-lg)",
  background: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const recommendedCard: CSSProperties = { border: "2px solid var(--color-border-info)" };
const scratchCard: CSSProperties = { border: "0.5px solid var(--color-border-tertiary)" };

const badge: CSSProperties = {
  position: "absolute",
  top: 12,
  right: 12,
  fontSize: 11,
  fontWeight: 500,
  padding: "2px 8px",
  borderRadius: 999,
  background: "var(--color-background-info)",
  color: "var(--color-text-info)",
};

const iconBox: CSSProperties = {
  width: 44,
  height: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--border-radius-md)",
};

const cardTitle: CSSProperties = { fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" };
const cardDesc: CSSProperties = { fontSize: 13, lineHeight: 1.5, color: "var(--color-text-secondary)" };

const chipRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: "auto" };
const chip: CSSProperties = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
};

const cta: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  marginTop: 4,
};

const infoBanner: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  fontSize: 12,
  color: "var(--color-text-secondary)",
};
